import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';

/**
 * Persistent "you are acting for someone else" banner (issue #44).
 *
 * Driven entirely by `delegation` from GET /api/auth/me, never by a local flag,
 * for the same reason ImpersonationBanner is: a local flag is lost on refresh,
 * leaving the session still acting for someone with nothing on screen saying so.
 *
 * A SEPARATE component from ImpersonationBanner, in a deliberately different
 * colour, because the two modes are not the same thing and must never be
 * confusable. Impersonating means you ARE somebody else and hold everything
 * they hold. Delegating means you are still yourself and have been lent two
 * narrow, consented-to capabilities -- which is what the wording here spells
 * out rather than leaving to be inferred.
 */
function DelegationBanner() {
  const { delegation, stopDelegation } = useAuth();
  const [stopping, setStopping] = useState(false);

  if (!delegation || !delegation.delegating_for) return null;

  const forUser = delegation.delegating_for;

  // Say exactly what was lent. "Acting for Jane" alone invites the reader to
  // assume it means everything Jane can do, which is the one thing it does not.
  const lent = [
    delegation.can_act_on_approvals && 'their approvals',
    delegation.can_edit_owned_documents && 'the documents they own',
  ].filter(Boolean).join(' and ');

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopDelegation();
      // No navigate(): the banner stops rendering once /me reports no
      // delegation, and throwing away the current page would usually discard
      // the very thing they came here to deal with.
    } finally {
      setStopping(false);
    }
  };

  return (
    <div className="delegation-banner" role="status">
      <span>
        🤝 Acting for <strong>{forUser.full_name}</strong> ({forUser.email})
        {lent ? <> — {lent} only</> : null}
      </span>
      <button className="btn btn-secondary" onClick={handleStop} disabled={stopping}>
        {stopping ? 'Stopping…' : `Stop acting for ${forUser.first_name || 'them'}`}
      </button>
    </div>
  );
}

export default DelegationBanner;
